import { writable, derived, get } from 'svelte/store';
import { v4 as uuidv4 } from 'uuid';

// --- Local Storage Helper ---
function createStoredWritable(key, initialValue) {
    const storedValue = localStorage.getItem(key);
    const data = storedValue ? JSON.parse(storedValue) : initialValue;
    const store = writable(data);

    store.subscribe(value => {
        localStorage.setItem(key, JSON.stringify(value));
    });

    return store;
}

// --- Toasts (Notifications) ---
export const toasts = writable([]);
export function showToast(message, type = 'success', duration = 3000) {
    const id = uuidv4();
    toasts.update(all => [...all, { id, message, type }]);
    setTimeout(() => {
        toasts.update(all => all.filter(t => t.id !== id));
    }, duration);
}

// --- DA Data Sources ---
export const officialDaSources = writable({});
export const customDaSources = createStoredWritable('customDaSources', {});

// New: DA Source Manifest
import { writable as svelteWritable } from 'svelte/store';
export const daSourceManifest = svelteWritable([]);

export async function loadDaSourceManifest() {
    try {
        const res = await fetch('/da-sources.json');
        if (res.ok) {
            const manifest = await res.json();
            daSourceManifest.set(manifest);
        } else {
            daSourceManifest.set([]);
        }
    } catch (e) {
        daSourceManifest.set([]);
    }
}

export const combinedDaSources = derived(
    [officialDaSources, customDaSources],
    ([$official, $custom]) => {
        return { ...$official, ...$custom };
    }
);

export const daSourceOptions = derived(
    [daSourceManifest, customDaSources],
    ([$manifest, $custom]) => {
        const options = [{ group: 'Default', name: 'Manual Entry', value: 'manual' }];
        if ($manifest.length > 0) {
            options.push({
                group: 'Official Lists',
                name: 'Official Lists',
                value: 'official',
                options: $manifest.map(item => ({ name: item.label, value: item.label }))
            });
        }
        // Custom logic unchanged
        const customNames = Object.keys($custom);
        if (customNames.length > 0) {
            options.push({
                group: 'Custom Lists',
                name: 'Custom Lists',
                value: 'custom',
                options: customNames.map(name => ({ name, value: name }))
            });
        }
        return options;
    }
);

export async function loadOfficialDaSources() {
    try {
        console.log("[Stores] Loading official DA sources...");
        const manifestResponse = await fetch('/da-sources.json');
        if (!manifestResponse.ok) {
            console.warn("DA Source manifest (da-sources.json) not found in /public folder.");
            showToast("Warning: DA sources manifest not found. Only manual entry will be available.", "warning");
            return;
        }

        const manifest = await manifestResponse.json();
        console.log("[Stores] Found DA source files:", manifest);
        const sources = {};

        for (const entry of manifest) {
            const filename = entry.filename;
            try {
                const res = await fetch(`/da_records/${filename}`);
                if (res.ok) {
                    const data = await res.json();
                    if (data && Array.isArray(data.da_data)) {
                        const cleanName = filename.replace('.json', '');
                        // Sort by effective date descending (newest first)
                        sources[cleanName] = data.da_data.sort((a, b) => {
                            const dateA = new Date(a.effective_date).getTime();
                            const dateB = new Date(b.effective_date).getTime();
                            return dateB - dateA;
                        });
                        console.log(`[Stores] Loaded DA source: ${cleanName} with ${data.da_data.length} rules`);
                    } else {
                        console.warn(`Invalid data structure in ${filename}`);
                    }
                } else {
                     console.warn(`Could not load DA file: ${filename}. Status: ${res.status}`);
                }
            } catch (e) {
                console.error(`Failed to load or parse ${filename}`, e);
            }
        }
        
        officialDaSources.set(sources);
        console.log("[Stores] Official DA sources loaded:", Object.keys(sources));
        
        if (Object.keys(sources).length === 0) {
            showToast("No DA sources loaded. Only manual entry will be available.", "warning");
        } else {
            showToast(`Loaded ${Object.keys(sources).length} DA sources successfully!`, "success");
        }
    } catch (error) {
        console.error("Could not load official DA sources:", error);
        showToast(`Failed to load DA sources: ${error.message}`, "error");
    }
}

export function getDARate(date, sourceName, salaryType) {
    if (sourceName === 'manual' || !sourceName) {
        console.log(`[Stores] Manual mode or no source specified for ${date.toISOString()}`);
        return 0;
    }
    
    try {
        const allSources = get(combinedDaSources);
        const daRules = allSources[sourceName];
        
        if (!daRules || !Array.isArray(daRules)) {
            console.warn(`[Stores] No DA rules found for source: ${sourceName}`);
            return 0;
        }

        // Find the most recent rule that applies to this date and salary type
        const applicableRule = daRules
            .filter(rule => {
                const ruleDate = new Date(rule.effective_date);
                return rule.type === salaryType && ruleDate <= date;
            })
            .sort((a, b) => {
                const dateA = new Date(a.effective_date).getTime();
                const dateB = new Date(b.effective_date).getTime();
                return dateB - dateA; // Sort descending (newest first)
            })[0];

        if (applicableRule) {
            console.log(`[Stores] Found DA rule for ${date.toISOString()}: ${applicableRule.to}% (effective: ${applicableRule.effective_date})`);
            return applicableRule.to;
        } else {
            console.log(`[Stores] No applicable DA rule found for ${date.toISOString()} with type ${salaryType}`);
            return 0;
        }
    } catch (error) {
        console.error(`[Stores] Error getting DA rate for ${date.toISOString()}:`, error);
        return 0;
    }
}


// --- Saved Calculations ---
export async function getDemoCalculationAsync() {
    // Wait for DA sources to be loaded
    if (get(daSourceManifest).length === 0) {
        await loadDaSourceManifest();
    }
    if (Object.keys(get(officialDaSources)).length === 0) {
        await loadOfficialDaSources();
    }
    // Use the first available DA source from the manifest
    const manifest = get(daSourceManifest);
    const daSourceEntry = manifest[0];
    const daSource = daSourceEntry ? daSourceEntry.label : 'manual';
    // Generate tableData for the demo card
    const startDate = '2021-01';
    const endDate = '2025-07';
    const basicPay = 24800;
    const salaryType = 'employee';
    const commutation = { opted: false, amount: null, startDate: '', endDate: '' };
    let newTableData = [];
    let currentDate = new Date(startDate + '-01T12:00:00Z');
    const endMonth = new Date(endDate + '-01T12:00:00Z');
    while (currentDate <= endMonth) {
        const monthYear = currentDate.toLocaleString('default', {
            month: 'long',
            year: 'numeric',
            timeZone: 'UTC',
        });
        // Get DA rate from the selected source
        let daRate = 0;
        try {
            daRate = getDARate(currentDate, daSource, salaryType);
        } catch (e) {
            daRate = 0;
        }
        const daAmount = Math.round(basicPay * (daRate / 100));
        const salaryDue = basicPay + daAmount;
        const drawn = 0;
        const arrears = 0;
        const difference = salaryDue - drawn;
        newTableData.push({
            monthYear,
            basicPay: parseFloat(String(basicPay)),
            daRate: parseFloat(String(daRate)),
            drawn,
            arrears,
            commuted: 0,
            daAmount,
            salaryDue,
            difference,
        });
        currentDate.setMonth(currentDate.getMonth() + 1);
    }
    return {
        id: 'demo-breakdown',
        name: 'Demo Breakdown',
        startDate,
        endDate,
        basicPay,
        salaryType,
        daSource,
        commutation,
        tableData: newTableData,
        createdAt: '2021-01-01T00:00:00.000Z',
        updatedAt: new Date().toISOString(),
        isDemo: true
    };
}

// Wrap the original store to always include the demo calculation
function withDemoCalculation(store) {
    const { subscribe, set, update } = store;
    return {
        subscribe(run, invalidate) {
            return subscribe(calcs => {
                // If demo card is missing, inject it at the start
                const hasDemo = calcs.some(c => c.id === 'demo-breakdown');
                if (!hasDemo) {
                    run([getDemoCalculationAsync(), ...calcs]);
                } else {
                    // Move demo to the top if not already
                    const sorted = [
                        ...calcs.filter(c => c.id === 'demo-breakdown'),
                        ...calcs.filter(c => c.id !== 'demo-breakdown')
                    ];
                    run(sorted);
                }
            }, invalidate);
        },
        set,
        update(fn) {
            // Prevent removal of demo card
            return update(calcs => {
                const filtered = calcs.filter(c => c.id !== 'demo-breakdown');
                const result = fn(filtered);
                // Always re-inject demo
                const hasDemo = result.some(c => c.id === 'demo-breakdown');
                return hasDemo ? result : [getDemoCalculationAsync(), ...result];
            });
        }
    };
}

function initializeWithDemoCard(key, initialValue) {
    const storedValue = localStorage.getItem(key);
    let data = storedValue ? JSON.parse(storedValue) : initialValue;
    // Only inject demo card if not present AND not deleted
    const demoDeleted = localStorage.getItem('demoCardDeleted') === 'true';
    // Async injection for demo card
    (async () => {
        if (!demoDeleted && !data.some(c => c.id === 'demo-breakdown')) {
            const demoCard = await getDemoCalculationAsync();
            data = [demoCard, ...data];
            localStorage.setItem(key, JSON.stringify(data));
        }
    })();
    const store = writable(data);
    store.subscribe(value => {
        localStorage.setItem(key, JSON.stringify(value));
    });
    return store;
}

export const savedCalculations = initializeWithDemoCard('savedCalculations', []);

export function saveCalculation(calculation) {
    const now = new Date().toISOString();
    calculation.updatedAt = now;

    savedCalculations.update(calcs => {
        const index = calcs.findIndex(c => c.id === calculation.id);
        if (index > -1) {
            calcs[index] = calculation;
        } else {
            calculation.id = uuidv4();
            calculation.createdAt = now;
            calcs.push(calculation);
        }
        return calcs;
    });
    showToast('Calculation saved!', 'success');
}

// Patch deleteCalculation to set the demoCardDeleted flag if the demo card is deleted
export function deleteCalculation(id) {
    if (id === 'demo-breakdown') {
        localStorage.setItem('demoCardDeleted', 'true');
    }
    savedCalculations.update(calcs => calcs.filter(c => c.id !== id));
    showToast('Calculation deleted.', 'info');
}

export function duplicateCalculation(id) {
    const calcs = get(savedCalculations);
    const original = calcs.find(c => c.id === id);
    if (original) {
        const newCalc = JSON.parse(JSON.stringify(original)); // Deep copy
        newCalc.id = uuidv4();
        newCalc.name += ' (Copy)';
        const now = new Date().toISOString();
        newCalc.createdAt = now;
        newCalc.updatedAt = now;
        savedCalculations.update(all => [...all, newCalc]);
        showToast('Calculation duplicated.', 'success');
    }
}

// --- Debug and Testing Functions ---
export function debugDaSources() {
    const official = get(officialDaSources);
    const custom = get(customDaSources);
    const combined = get(combinedDaSources);
    
    console.log("=== DA Sources Debug Info ===");
    console.log("Official sources:", Object.keys(official));
    console.log("Custom sources:", Object.keys(custom));
    console.log("Combined sources:", Object.keys(combined));
    
    // Test DA rate calculation for a sample date
    const testDate = new Date('2024-01-01');
    const testSource = Object.keys(official)[0];
    if (testSource) {
        const rate = getDARate(testDate, testSource, 'employee');
        console.log(`Sample DA rate for ${testDate.toISOString()} using ${testSource}: ${rate}%`);
    }
}